/* Each companion card adds only its selected variant. Never retry a cart POST. */
(() => {
  async function addInlineRecommendation({ variantId, drawer, action, pageUrl, opener }, environment = globalThis) {
    if (!/^[1-9]\d*$/.test(String(variantId))) throw new Error('Please choose an available option.');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function' || typeof drawer.renderContents !== 'function') {
      throw new Error('Your cart is still loading. Please try again in a moment.');
    }
    const page = new URL(pageUrl);
    const endpoint = new URL(action, page);
    if (endpoint.origin !== page.origin || !/\/cart\/add(?:\.js)?\/?$/.test(endpoint.pathname)) {
      throw new Error('Unable to connect to this store’s cart.');
    }
    endpoint.pathname = endpoint.pathname.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
    const descriptors = drawer.getSectionsToRender();
    const sections = [...new Set(descriptors.map(section => section.id))];
    if (!sections.length || sections.length > 5) throw new Error('Please refresh the page before adding this item.');
    const valid = html => descriptors.every(section => typeof html?.[section.id] === 'string' &&
      new environment.DOMParser().parseFromString(html[section.id], 'text/html').querySelector(section.selector || '.shopify-section'));
    let added = false;
    let rejected = '';
    try {
      const response = await environment.fetch(endpoint.href, {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: String(variantId), quantity: 1 }], sections, sections_url: page.pathname + page.search })
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        rejected = typeof state.description === 'string' ? state.description : typeof state.message === 'string' ? state.message : '';
        throw new Error('Cart add could not be confirmed.');
      }
      added = true;
      if (!valid(state.sections)) {
        const refreshUrl = new URL(page);
        refreshUrl.searchParams.set('sections', sections.join(','));
        const refreshed = await environment.fetch(refreshUrl.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!refreshed.ok) throw new Error('Cart refresh unavailable.');
        state.sections = await refreshed.json();
      }
      if (!valid(state.sections)) throw new Error('Cart refresh unavailable.');
      drawer.setActiveElement?.(opener);
      drawer.renderContents(state);
      drawer.classList.remove('is-empty');
      return state;
    } catch (failure) {
      const error = new Error(added
        ? 'Added to your cart, but the drawer could not refresh. Please check your cart before adding again.'
        : (rejected ? rejected + ' ' : 'We could not confirm the add. ') + 'Please check your cart before trying again.');
      error.cartAdded = added;
      error.checkCart = true;
      throw error;
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { addInlineRecommendation };
  if (typeof customElements === 'undefined' || customElements.get('aven-inline-recommendations')) return;
  class AvenInlineRecommendations extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.addEventListener('change', event => {
        const select = event.target.closest('[data-air-select]');
        if (!select || this.busy) return;
        const card = select.closest('[data-air-card]');
        const selected = select.selectedOptions[0];
        if (!card || !selected) return;
        card.querySelector('[data-air-price]').textContent = selected.dataset.money;
        const compare = card.querySelector('[data-air-compare]');
        compare.textContent = selected.dataset.compareMoney;
        compare.hidden = selected.dataset.onSale !== 'true';
        const image = card.querySelector('.aven-inline-rec__image');
        if (image && selected.dataset.image) {
          image.removeAttribute('srcset');
          image.src = selected.dataset.image;
        }
        const button = card.querySelector('[data-air-submit]');
        if (!button.dataset.cartUnconfirmed) {
          button.disabled = false;
          button.querySelector('[data-air-button-text]').textContent = this.dataset.addLabel || 'Add to cart';
        }
      }, { signal: this.abort.signal });
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-air-form]');
        if (!form) return;
        event.preventDefault();
        event.stopPropagation();
        this.add(form);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
    async add(form) {
      if (this.busy) return;
      const select = form.querySelector('[data-air-select]');
      const option = select?.selectedOptions[0];
      const button = form.querySelector('[data-air-submit]');
      if (!option || option.disabled || !option.value || button.disabled) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      const controls = [...this.querySelectorAll('button, select')];
      const originalDisabled = new Map(controls.map(control => [control, control.disabled]));
      controls.forEach(control => { control.disabled = true; });
      const label = button.querySelector('[data-air-button-text]');
      const status = this.querySelector('[data-air-status]');
      const recovery = this.querySelector('[data-air-recovery]');
      label.textContent = 'Adding…';
      status.hidden = true;
      recovery.hidden = true;
      let added = false;
      let unconfirmed = false;
      try {
        const drawer = document.querySelector('cart-drawer');
        await addInlineRecommendation({ variantId: option.value, drawer, action: form.action, pageUrl: window.location.href, opener: button });
        added = true;
        const title = form.closest('[data-air-card]').querySelector('.aven-inline-rec__title').textContent.trim();
        status.textContent = title + ' added to your cart.';
        status.hidden = false;
      } catch (error) {
        added = !!error.cartAdded;
        unconfirmed = !!error.checkCart;
        status.textContent = error.message || 'Please check your cart before trying again.';
        status.hidden = false;
        recovery.hidden = !error.checkCart;
      } finally {
        this.removeAttribute('aria-busy');
        controls.forEach(control => { control.disabled = originalDisabled.get(control); });
        // On an ambiguous response prevent a repeat add until the shopper has
        // checked/reloaded their cart. No timeout or hidden automatic retry.
        if (unconfirmed) {
          button.disabled = true;
          button.dataset.cartUnconfirmed = 'true';
        }
        label.textContent = added && unconfirmed ? 'Added' : this.dataset.addLabel || 'Add to cart';
        this.busy = false;
      }
    }
  }
  customElements.define('aven-inline-recommendations', AvenInlineRecommendations);
})();
